import { createClient } from "@supabase/supabase-js";

const _url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const _key = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!_url) throw new Error("VITE_SUPABASE_URL is required — check .env");
if (!_key) throw new Error("VITE_SUPABASE_ANON_KEY is required — check .env");

export const SUPABASE_URL: string = _url;
export const SUPABASE_ANON_KEY: string = _key;

// Shared Supabase JS client — handles session storage and auto token refresh.
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

export async function getFreshToken(): Promise<string> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) {
      const expiresAt = session.expires_at ?? 0;
      const nowSec = Math.floor(Date.now() / 1000);
      if (expiresAt - nowSec > 60) {
        localStorage.setItem("studio_token", session.access_token);
        return session.access_token;
      }
      // Token expires within 60 s — refresh
      const { data: refreshData } = await supabase.auth.refreshSession();
      if (refreshData?.session?.access_token) {
        localStorage.setItem("studio_token", refreshData.session.access_token);
        return refreshData.session.access_token;
      }
    }
  } catch { /* fall through */ }
  // Bug F: do NOT fall back to a stale localStorage token. A dead/rotated
  // session whose access_token still sits in localStorage is exactly what fed
  // the WS verifyJWT "Auth session missing!" reconnect storm — the server
  // rejects the dead token, y-websocket retries every ~3 s forever. Returning
  // "" makes callers source a live token (or stop) instead of replaying a dead
  // one. We do NOT removeItem here: getFreshToken runs on benign mount paths
  // where getSession() can be momentarily empty during init, and clearing
  // would nuke a still-valid token. The authoritative dead-session clear lives
  // in refreshTokenOrClear() below.
  return "";
}

// Force a session refresh (bypasses getFreshToken's 60s-to-expiry gate) and
// report whether a live session remains. Used by the WS reconnect path: on a
// 4001 auth-close, this rotates the token if the session is still alive, or
// returns "" — and clears the dead token — when the session is truly gone
// (signed out / revoked server-side), so the caller can stop reconnecting and
// re-authenticate instead of looping on a dead token.
export async function refreshTokenOrClear(): Promise<string> {
  try {
    const { data } = await supabase.auth.refreshSession();
    const tok = data?.session?.access_token;
    if (tok) { localStorage.setItem("studio_token", tok); return tok; }
  } catch { /* fall through to clear */ }
  localStorage.removeItem("studio_token");
  return "";
}
